import { z } from 'zod';

import { AddressSchema } from './party.js';

// -----------------------------------------------------------------------------
// Enums (mirror the SQL enum lists from migration 20260517000001_phase5_hr.ts)
// -----------------------------------------------------------------------------

export const EmploymentTypeSchema = z.enum(['full_time', 'part_time', 'contract', 'intern', 'terminated']);
export type EmploymentType = z.infer<typeof EmploymentTypeSchema>;

export const EmployeeStatusSchema = z.enum(['active', 'on_leave', 'suspended', 'terminated']);
export type EmployeeStatus = z.infer<typeof EmployeeStatusSchema>;

export const GenderSchema = z.enum(['male', 'female', 'other', 'unspecified']);
export type Gender = z.infer<typeof GenderSchema>;

export const MaritalStatusSchema = z.enum(['single', 'married', 'divorced', 'widowed', 'unspecified']);
export type MaritalStatus = z.infer<typeof MaritalStatusSchema>;

export const CompensationTypeSchema = z.enum(['salary_monthly', 'salary_annual', 'hourly']);
export type CompensationType = z.infer<typeof CompensationTypeSchema>;

export const LeaveAccrualStrategySchema = z.enum(['none', 'monthly_fixed', 'annual_fixed', 'unlimited']);
export type LeaveAccrualStrategy = z.infer<typeof LeaveAccrualStrategySchema>;

export const LeaveRequestStatusSchema = z.enum(['draft', 'submitted', 'approved', 'rejected', 'cancelled']);
export type LeaveRequestStatus = z.infer<typeof LeaveRequestStatusSchema>;

export const TimeEntryStatusSchema = z.enum(['draft', 'submitted', 'approved', 'rejected']);
export type TimeEntryStatus = z.infer<typeof TimeEntryStatusSchema>;

export const PayrollRunStatusSchema = z.enum(['draft', 'approved', 'posted', 'cancelled']);
export type PayrollRunStatus = z.infer<typeof PayrollRunStatusSchema>;

export const PayslipLineKindSchema = z.enum(['earning', 'deduction']);
export type PayslipLineKind = z.infer<typeof PayslipLineKindSchema>;

export const TaxEngineSchema = z.enum(['generic', 'iraq']);
export type TaxEngine = z.infer<typeof TaxEngineSchema>;

// -----------------------------------------------------------------------------
// Common helpers
// -----------------------------------------------------------------------------

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const positiveDecimalString = z.string().regex(/^\d+(\.\d{1,4})?$/, 'expected non-negative decimal');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);

// -----------------------------------------------------------------------------
// Department + Position
// -----------------------------------------------------------------------------

export const DepartmentCreateSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  parentId: z.string().uuid().nullable().optional(),
  managerEmployeeId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type DepartmentCreate = z.infer<typeof DepartmentCreateSchema>;

export const DepartmentUpdateSchema = DepartmentCreateSchema.partial();
export type DepartmentUpdate = z.infer<typeof DepartmentUpdateSchema>;

export const DepartmentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  parentId: z.string().uuid().nullable(),
  managerEmployeeId: z.string().uuid().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Department = z.infer<typeof DepartmentSchema>;

export const PositionCreateSchema = z.object({
  code: z.string().min(1).max(64),
  title: z.string().min(1).max(200),
  description: z.string().max(2_000).nullable().optional(),
  payGrade: z.string().max(32).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type PositionCreate = z.infer<typeof PositionCreateSchema>;

export const PositionUpdateSchema = PositionCreateSchema.partial();
export type PositionUpdate = z.infer<typeof PositionUpdateSchema>;

export const PositionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  payGrade: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Position = z.infer<typeof PositionSchema>;

// -----------------------------------------------------------------------------
// Employee
// -----------------------------------------------------------------------------

export const EmployeeCreateSchema = z.object({
  code: z.string().min(1).max(64),
  firstName: z.string().min(1).max(120),
  lastName: z.string().min(1).max(120),
  displayName: z.string().min(1).max(240).optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  dateOfBirth: dateString.nullable().optional(),
  nationalId: z.string().max(64).nullable().optional(),
  gender: GenderSchema.optional(),
  maritalStatus: MaritalStatusSchema.optional(),
  address: AddressSchema.nullable().optional(),
  employmentType: EmploymentTypeSchema.optional(),
  hireDate: dateString,
  managerId: z.string().uuid().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  positionId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  defaultCurrencyCode: currencyCode,
  notes: z.string().max(2_000).nullable().optional(),
  // Attendance: the schedule judged against (null = the tenant default), the
  // home branch, and whether the person is tracked at all — an owner who never
  // punches is switched off rather than shown absent every day.
  workScheduleId: z.string().uuid().nullable().optional(),
  warehouseId: z.string().uuid().nullable().optional(),
  trackAttendance: z.boolean().optional(),
  /** The number a fingerprint device enrolled this person as (1, 2, 3…). */
  deviceUserId: z.string().trim().max(32).nullable().optional(),
});
export type EmployeeCreate = z.infer<typeof EmployeeCreateSchema>;

export const EmployeeUpdateSchema = z.object({
  firstName: z.string().min(1).max(120).optional(),
  lastName: z.string().min(1).max(120).optional(),
  displayName: z.string().min(1).max(240).optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().max(32).nullable().optional(),
  dateOfBirth: dateString.nullable().optional(),
  nationalId: z.string().max(64).nullable().optional(),
  gender: GenderSchema.optional(),
  maritalStatus: MaritalStatusSchema.optional(),
  address: AddressSchema.nullable().optional(),
  employmentType: EmploymentTypeSchema.optional(),
  status: EmployeeStatusSchema.optional(),
  terminationDate: dateString.nullable().optional(),
  terminationReason: z.string().max(200).nullable().optional(),
  managerId: z.string().uuid().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  positionId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid().nullable().optional(),
  defaultCurrencyCode: currencyCode.optional(),
  notes: z.string().max(2_000).nullable().optional(),
  workScheduleId: z.string().uuid().nullable().optional(),
  warehouseId: z.string().uuid().nullable().optional(),
  trackAttendance: z.boolean().optional(),
  deviceUserId: z.string().trim().max(32).nullable().optional(),
});
export type EmployeeUpdate = z.infer<typeof EmployeeUpdateSchema>;

export const EmployeeSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  userId: z.string().uuid().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  nationalId: z.string().nullable(),
  gender: GenderSchema,
  maritalStatus: MaritalStatusSchema,
  address: AddressSchema.nullable(),
  employmentType: EmploymentTypeSchema,
  status: EmployeeStatusSchema,
  hireDate: z.string(),
  terminationDate: z.string().nullable(),
  terminationReason: z.string().nullable(),
  managerId: z.string().uuid().nullable(),
  departmentId: z.string().uuid().nullable(),
  positionId: z.string().uuid().nullable(),
  defaultCurrencyCode: z.string(),
  notes: z.string().nullable(),
  workScheduleId: z.string().uuid().nullable(),
  warehouseId: z.string().uuid().nullable(),
  trackAttendance: z.boolean(),
  /** Whether a kiosk PIN is set. The PIN itself never leaves the server. */
  hasAttendancePin: z.boolean(),
  deviceUserId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Employee = z.infer<typeof EmployeeSchema>;

// -----------------------------------------------------------------------------
// Compensation
// -----------------------------------------------------------------------------

export const CompensationCreateSchema = z.object({
  effectiveFrom: dateString,
  effectiveTo: dateString.nullable().optional(),
  compType: CompensationTypeSchema,
  amount: positiveDecimalString,
  currencyCode,
  reason: z.string().max(200).nullable().optional(),
});
export type CompensationCreate = z.infer<typeof CompensationCreateSchema>;

export const CompensationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  employeeId: z.string().uuid(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  compType: CompensationTypeSchema,
  amount: z.string(),
  currencyCode: z.string(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Compensation = z.infer<typeof CompensationSchema>;

// -----------------------------------------------------------------------------
// Leave types + balances + requests
// -----------------------------------------------------------------------------

export const LeaveTypeCreateSchema = z.object({
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(120),
  isPaid: z.boolean().optional(),
  accrualStrategy: LeaveAccrualStrategySchema,
  accrualAmount: positiveDecimalString.nullable().optional(),
  defaultAnnualBalance: positiveDecimalString.nullable().optional(),
  carryOverMax: positiveDecimalString.nullable().optional(),
  isActive: z.boolean().optional(),
});
export type LeaveTypeCreate = z.infer<typeof LeaveTypeCreateSchema>;

export const LeaveTypeUpdateSchema = LeaveTypeCreateSchema.partial();
export type LeaveTypeUpdate = z.infer<typeof LeaveTypeUpdateSchema>;

export const LeaveTypeSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  isPaid: z.boolean(),
  accrualStrategy: LeaveAccrualStrategySchema,
  accrualAmount: z.string().nullable(),
  defaultAnnualBalance: z.string().nullable(),
  carryOverMax: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LeaveType = z.infer<typeof LeaveTypeSchema>;

export const LeaveBalanceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  employeeId: z.string().uuid(),
  leaveTypeId: z.string().uuid(),
  year: z.number().int(),
  entitled: z.string(),
  consumed: z.string(),
  pending: z.string(),
  updatedAt: z.string(),
});
export type LeaveBalance = z.infer<typeof LeaveBalanceSchema>;

export const LeaveRequestCreateSchema = z.object({
  employeeId: z.string().uuid().optional(),    // omitted = caller's employee
  leaveTypeId: z.string().uuid(),
  startDate: dateString,
  endDate: dateString,
  reason: z.string().max(500).optional(),
});
export type LeaveRequestCreate = z.infer<typeof LeaveRequestCreateSchema>;

export const LeaveRequestDecisionSchema = z.object({
  note: z.string().max(500).optional(),
});
export type LeaveRequestDecision = z.infer<typeof LeaveRequestDecisionSchema>;

export const LeaveRequestSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  employeeId: z.string().uuid(),
  leaveTypeId: z.string().uuid(),
  startDate: z.string(),
  endDate: z.string(),
  daysRequested: z.string(),
  reason: z.string().nullable(),
  status: LeaveRequestStatusSchema,
  approverEmployeeId: z.string().uuid().nullable(),
  decidedAt: z.string().nullable(),
  decidedByUserId: z.string().uuid().nullable(),
  decisionNote: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LeaveRequest = z.infer<typeof LeaveRequestSchema>;

// -----------------------------------------------------------------------------
// Time entries
// -----------------------------------------------------------------------------

export const TimeEntryCreateSchema = z.object({
  employeeId: z.string().uuid().optional(),     // omitted = caller's employee
  entryDate: dateString,
  hours: z.string().regex(/^\d+(\.(00|25|50|75))?$/, 'hours must be in 0.25 increments'),
  projectCode: z.string().max(64).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type TimeEntryCreate = z.infer<typeof TimeEntryCreateSchema>;

export const TimeEntryUpdateSchema = TimeEntryCreateSchema.partial();
export type TimeEntryUpdate = z.infer<typeof TimeEntryUpdateSchema>;

export const TimeEntrySchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  employeeId: z.string().uuid(),
  entryDate: z.string(),
  hours: z.string(),
  projectCode: z.string().nullable(),
  notes: z.string().nullable(),
  status: TimeEntryStatusSchema,
  approverEmployeeId: z.string().uuid().nullable(),
  decidedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TimeEntry = z.infer<typeof TimeEntrySchema>;

// -----------------------------------------------------------------------------
// Payroll
// -----------------------------------------------------------------------------

export const PayrollRunCreateSchema = z.object({
  periodStart: dateString,
  periodEnd: dateString,
  payDate: dateString,
  employeeIds: z.array(z.string().uuid()).min(1).max(1_000).optional(),
});
export type PayrollRunCreate = z.infer<typeof PayrollRunCreateSchema>;

export const PayrollRunSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  runNumber: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  payDate: z.string(),
  baseCurrencyCode: z.string(),
  status: PayrollRunStatusSchema,
  grossTotal: z.string(),
  deductionsTotal: z.string(),
  netTotal: z.string(),
  postedJournalEntryId: z.string().uuid().nullable(),
  postedAt: z.string().nullable(),
  postedByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PayrollRun = z.infer<typeof PayrollRunSchema>;

export const PayslipLineSchema = z.object({
  lineNo: z.number().int(),
  kind: PayslipLineKindSchema,
  code: z.string(),
  description: z.string().nullable(),
  amount: z.string(),
  /** Days (ABSENCE) or minutes (LATE, OVERTIME, HOURS) behind an attendance line; null elsewhere. */
  quantity: z.string().nullable().optional(),
});
export type PayslipLine = z.infer<typeof PayslipLineSchema>;

export const PayslipSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  payrollRunId: z.string().uuid(),
  employeeId: z.string().uuid(),
  currencyCode: z.string(),
  gross: z.string(),
  deductionsTotal: z.string(),
  net: z.string(),
  fxRateToBase: z.string(),
  baseGross: z.string(),
  baseNet: z.string(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(PayslipLineSchema).optional(),
});
export type Payslip = z.infer<typeof PayslipSchema>;

// -----------------------------------------------------------------------------
// HR tenant settings
// -----------------------------------------------------------------------------

export const HrSettingsSchema = z.object({
  payrollPeriod: z.enum(['monthly', 'biweekly', 'weekly']),
  payDayOfMonth: z.number().int().min(1).max(31),
  fiscalYearStartMonth: z.number().int().min(1).max(12),
  taxEngine: TaxEngineSchema,
  personalAllowance: positiveDecimalString.nullable().optional(),
  glAccounts: z.object({
    salariesExpense: z.string().uuid().nullable(),
    payrollPayable: z.string().uuid().nullable(),
    employerTaxExpense: z.string().uuid().nullable().optional(),
  }),
});
export type HrSettings = z.infer<typeof HrSettingsSchema>;

export const HrSettingsUpdateSchema = HrSettingsSchema.partial();
export type HrSettingsUpdate = z.infer<typeof HrSettingsUpdateSchema>;
